import React, { useState } from 'react'
import { usePromoCodesQuery } from '@graphql/queries/__generated__/promo-codes.generated'
import { maybe } from '../../../utils/misc'
import { PromoCodeFragment } from '@graphql/fragments/__generated__/PromoCode.generated'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Button, Collapse, Dialog, DialogContent, DialogTitle } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { Link } from 'react-router-dom'
import { getSearchUrl } from '../../../urls'
import { useQueryParams, StringParam } from 'use-query-params'
import { useTranslation } from 'react-i18next'

interface Props {
  hide?: boolean
}

export const PROMO_CODES_DIALOG_PARAMS = {
  promoCodesDialog: StringParam,
}

const PromoCodes: React.FC<Props> = ({ hide }) => {
  const { t } = useTranslation()
  const [params, setParams] = useQueryParams(PROMO_CODES_DIALOG_PARAMS)
  const promoCodesQueryResult = usePromoCodesQuery({
    variables: {
      first: 10,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  })

  const promoCodes = maybe<PromoCodeFragment[]>(
    () => promoCodesQueryResult.data.promoCodes.edges.map(edge => edge.node),
    []
  )

  const swiperOptions: SwiperOptions = {
    spaceBetween: 0,
    slidesPerView: 1,
    slidesPerGroup: 1,
    speed: 500,
    lazy: true,
    preloadImages: false,
    watchSlidesProgress: true,
    loop: true,
    allowSlideNext: promoCodes.length > 1,
    allowSlidePrev: promoCodes.length > 1,
    autoplay: {
      disableOnInteraction: true,
      delay: 10000,
      waitForTransition: true,
    },
  }
  const onOpen = (id: string) => () => {
    setParams({ promoCodesDialog: id }, 'replaceIn')
  }
  const onClose = () => {
    setParams({ promoCodesDialog: undefined }, 'replaceIn')
  }
  const onToggleDetails = (id: string) => () => {
    setParams({ promoCodesDialog: id }, 'replaceIn')
  }
  if (hide || !promoCodes.length) {
    return null
  }
  return (
    <div className='relative'>
      <Swiper {...swiperOptions}>
        {promoCodes.map(node => {
          const style = JSON.parse(node.style)
          const backgroundColor = style['background_color'] || '#000'
          const color = style['text_color'] || '#fff'
          return (
            <SwiperSlide key={node.id}>
              <div
                className='flex justify-center font-bold pt-4 pb-2 text-11 uppercase text-center tracking-wider'
                style={{ backgroundColor, color, lineHeight: '17px' }}
              >
                <Link
                  className='no-underline'
                  to={`${getSearchUrl('', [], [node.pk])}&sortBy=most_recent&sortByDirection=DESC`}
                >
                  {node.name}
                </Link>
                &nbsp;|&nbsp;
                <span
                  className='cursor-pointer normal-case'
                  style={{ color, lineHeight: '17px' }}
                  onClick={onOpen(node.id)}
                >
                  {t('button.seeAllOffers', 'See All Offers')}
                </span>
              </div>
            </SwiperSlide>
          )
        })}
      </Swiper>
      <Dialog open={!!params.promoCodesDialog} onClose={onClose}>
        <DialogTitle>
          <span className='text-capitalize font-bold'>{t('title.saleOffers', 'Sale & Offers')}</span>
          <span className='absolute right-10 top-10'>
            <IconButton onClick={onClose} title={t('close', 'Close')}>
              <CloseIcon fontSize='inherit' />
            </IconButton>
          </span>
        </DialogTitle>
        <DialogContent dividers>
          <div className='w-600 max-w-full'>
            {promoCodes.map(node => {
              return (
                <div key={node.id} className='mb-20 text-13'>
                  <div className='text-red font-semibold text-base'>
                    <Link
                      className='no-underline'
                      onClick={onClose}
                      to={`${getSearchUrl('', [], [node.pk])}&sortBy=most_recent&sortByDirection=DESC`}
                    >
                      {node.name}
                    </Link>
                  </div>
                  <div className='my-5 flex items-center -mx-16'>
                    <Button
                      variant='text'
                      className='transparent'
                      onClick={onToggleDetails(node.id)}
                      disableElevation
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                    >
                      <span className='normal-case text-underline text-gray text-13'>
                        {params.promoCodesDialog === node.id
                          ? t('button.hideDetails', 'Hide details')
                          : t('button.details', 'Details')}
                      </span>
                    </Button>
                    <Button
                      component={Link}
                      onClick={onClose}
                      variant='text'
                      className='transparent'
                      to={`${getSearchUrl('', [], [node.pk])}&sortBy=most_recent&sortByDirection=DESC`}
                      disableElevation
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                    >
                      <span className='normal-case text-underline text-gray text-13'>
                        {t('button.shopNow', 'Shop now')}
                      </span>
                    </Button>
                  </div>
                  <Collapse in={params.promoCodesDialog === node.id}>
                    <div>{node.description}</div>
                  </Collapse>
                </div>
              )
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(PromoCodes)
